import React, { useMemo, useState } from "react";
import { useHistory, useParams } from "react-router-dom";
import { useSelector } from "react-redux";
import {
  Button,
  Input,
  Modal,
  Select,
  Space,
  message,
} from "antd";
import {
  ArrowLeftOutlined,
  RedoOutlined,
  UndoOutlined,
  ZoomInOutlined,
  ZoomOutOutlined,
} from "@ant-design/icons";
import { arrayMove } from "@dnd-kit/sortable";
import { Locale } from "../../../utils";
import { getUserInfo } from "../../../utils/redux/reducers/User";
import {
  createComponent,
  createRow,
  packComponentsIntoRows,
  rowUsedSpan,
  tryAppendBesideTall,
  flattenRowChildren,
  uid,
  isTallMedia,
  removeLeafFromRows,
  insertLeafIntoStack,
  insertLeafBesideTall,
  insertLeafAsRow,
} from "./catalog";
import { SAMPLE_ORDER } from "./sampleOrder";
import { getTemplate, saveTemplate } from "./storage";
import ComponentPalette from "./components/ComponentPalette";
import ComponentOutline from "./components/ComponentOutline";
import PropertyPanel from "./components/PropertyPanel";
import TemplateCanvas from "./components/TemplateCanvas";
import "./PrintTemplateDesigner.scss";

const i18n = Locale.getInstance();
const { Option } = Select;

function findComp(rows, id) {
  for (let r = 0; r < rows.length; r++) {
    const children = rows[r].children || [];
    for (let c = 0; c < children.length; c++) {
      const child = children[c];
      if (child.id === id) {
        return {
          rowIndex: r,
          colIndex: c,
          stackIndex: null,
          comp: child,
          stack: null,
        };
      }
      if (child.type === "stack") {
        const stackChildren = child.children || [];
        for (let s = 0; s < stackChildren.length; s++) {
          if (stackChildren[s].id === id) {
            return {
              rowIndex: r,
              colIndex: c,
              stackIndex: s,
              comp: stackChildren[s],
              stack: child,
            };
          }
        }
      }
    }
  }
  return null;
}

function mapRowsUpdateComp(rows, nextComp) {
  return rows.map((row) => ({
    ...row,
    children: (row.children || []).map((c) => {
      if (c.id === nextComp.id) return nextComp;
      if (c.type === "stack") {
        return {
          ...c,
          children: (c.children || []).map((s) =>
            s.id === nextComp.id ? nextComp : s
          ),
        };
      }
      return c;
    }),
  }));
}

function flattenLeaves(rows) {
  return rows.flatMap((r) => flattenRowChildren(r.children || []));
}

export default function PrintTemplateDesigner() {
  const { id } = useParams();
  const history = useHistory();
  const user = useSelector((state) => getUserInfo(state));
  const merchantId = user?.merchant?.id;

  const previewOrder = useMemo(
    () => ({
      ...SAMPLE_ORDER,
      merchant: user?.merchant || SAMPLE_ORDER.merchant,
    }),
    [user?.merchant]
  );

  const initial = useMemo(() => {
    const tpl = getTemplate(merchantId, id);
    return tpl ? JSON.parse(JSON.stringify(tpl)) : null;
  }, [merchantId, id]);

  const [template, setTemplate] = useState(initial);
  const [selectedId, setSelectedId] = useState(null);
  const [zoom, setZoom] = useState(1);
  const [historyStack, setHistoryStack] = useState([]);
  const [futureStack, setFutureStack] = useState([]);

  if (!template) {
    return (
      <div className="print-template-designer print-template-designer--missing">
        <p>{i18n.t("order.printTemplate.notFound")}</p>
        <Button onClick={() => history.push("/main/order/print-templates")}>
          {i18n.t("order.printTemplate.backToList")}
        </Button>
      </div>
    );
  }

  const pushHistory = (prev) => {
    setHistoryStack((h) => [...h.slice(-29), JSON.parse(JSON.stringify(prev))]);
    setFutureStack([]);
  };

  const updateTemplate = (updater) => {
    setTemplate((prev) => {
      pushHistory(prev);
      const next = typeof updater === "function" ? updater(prev) : updater;
      return next;
    });
  };

  const undo = () => {
    setHistoryStack((h) => {
      if (!h.length) return h;
      const prev = h[h.length - 1];
      setFutureStack((f) => [JSON.parse(JSON.stringify(template)), ...f]);
      setTemplate(prev);
      return h.slice(0, -1);
    });
  };

  const redo = () => {
    setFutureStack((f) => {
      if (!f.length) return f;
      const next = f[0];
      setHistoryStack((h) => [...h, JSON.parse(JSON.stringify(template))]);
      setTemplate(next);
      return f.slice(1);
    });
  };

  const selectedFound = selectedId
    ? findComp(template.layout.rows, selectedId)
    : null;
  const selected = selectedFound?.comp || null;

  const onAdd = (paletteItem) => {
    const comp = createComponent(paletteItem);
    if (template.paperSize !== "A4" && comp.span === 12) {
      comp.span = 8;
    }
    updateTemplate((prev) => {
      let rows = prev.layout.rows.map((r) => ({
        ...r,
        children: [...(r.children || [])],
      }));
      const selected = selectedId ? findComp(rows, selectedId) : null;

      // Prefer stacking beside a selected / nearby tall QR
      const beside = tryAppendBesideTall(
        rows,
        comp,
        selected ? selected.rowIndex : -1
      );
      if (beside) {
        return { ...prev, layout: { ...prev.layout, rows: beside } };
      }

      const span = Math.min(12, Math.max(1, comp.span || 12));
      const tryInsert = (rowIndex) => {
        if (rowIndex < 0 || rowIndex >= rows.length) return false;
        if (rowUsedSpan(rows[rowIndex]) + span > 12) return false;
        rows[rowIndex] = {
          ...rows[rowIndex],
          children: [...rows[rowIndex].children, comp],
        };
        return true;
      };
      if (selected && tryInsert(selected.rowIndex)) {
        return { ...prev, layout: { ...prev.layout, rows } };
      }
      if (rows.length && tryInsert(rows.length - 1)) {
        return { ...prev, layout: { ...prev.layout, rows } };
      }
      return {
        ...prev,
        layout: { ...prev.layout, rows: [...rows, createRow([comp])] },
      };
    });
    setSelectedId(comp.id);
  };

  const onChangeComp = (nextComp) => {
    updateTemplate((prev) => {
      const found = findComp(prev.layout.rows, nextComp.id);
      if (!found) return prev;

      // Nested in side stack: edit the leaf itself (span is within the side grid)
      if (found.stack) {
        const rows = mapRowsUpdateComp(prev.layout.rows, nextComp);
        return { ...prev, layout: { ...prev.layout, rows } };
      }

      const spanChanged =
        Number(found.comp.span || 12) !== Number(nextComp.span || 12);
      const besideChanged =
        Number(found.comp.besideRows ?? 3) !==
        Number(nextComp.besideRows ?? 3);
      let rows = mapRowsUpdateComp(prev.layout.rows, nextComp);
      if (!spanChanged && !besideChanged) {
        return { ...prev, layout: { ...prev.layout, rows } };
      }
      const flat = flattenLeaves(rows);
      return {
        ...prev,
        layout: { ...prev.layout, rows: packComponentsIntoRows(flat) },
      };
    });
  };

  const onDeleteSelected = () => {
    if (!selectedId) return;
    updateTemplate((prev) => {
      const rows = prev.layout.rows
        .map((row) => {
          const children = [];
          (row.children || []).forEach((c) => {
            if (c.id === selectedId) return;
            if (c.type === "stack") {
              const nextStackChildren = (c.children || []).filter(
                (s) => s.id !== selectedId
              );
              if (!nextStackChildren.length) return;
              if (nextStackChildren.length === 1) {
                children.push({
                  ...nextStackChildren[0],
                  span: c.span || nextStackChildren[0].span,
                });
              } else {
                children.push({ ...c, children: nextStackChildren });
              }
              return;
            }
            children.push(c);
          });
          return { ...row, children };
        })
        .filter((row) => (row.children || []).length > 0);
      return { ...prev, layout: { ...prev.layout, rows } };
    });
    setSelectedId(null);
  };

  const onCopySelected = () => {
    const found = findComp(template.layout.rows, selectedId);
    if (!found || found.comp.type === "stack") return;
    const copy = {
      ...JSON.parse(JSON.stringify(found.comp)),
      id: uid("comp"),
    };
    updateTemplate((prev) => {
      const rows = prev.layout.rows.map((row, ri) => {
        if (ri !== found.rowIndex) return row;
        const children = [...(row.children || [])];
        if (found.stackIndex != null) {
          const stack = children[found.colIndex];
          const stackChildren = [...(stack.children || [])];
          stackChildren.splice(found.stackIndex + 1, 0, copy);
          children[found.colIndex] = { ...stack, children: stackChildren };
        } else {
          children.splice(found.colIndex + 1, 0, copy);
        }
        return { ...row, children };
      });
      return { ...prev, layout: { ...prev.layout, rows } };
    });
    setSelectedId(copy.id);
  };

  const onMoveSelected = (dir) => {
    const found = findComp(template.layout.rows, selectedId);
    if (!found) return;
    updateTemplate((prev) => {
      // Inside beside stack: move within stack only (easy, no re-fold)
      if (found.stack && found.stackIndex != null) {
        const kids = [...(found.stack.children || [])];
        const target = found.stackIndex + dir;
        if (target >= 0 && target < kids.length) {
          const nextKids = arrayMove(kids, found.stackIndex, target);
          const rows = prev.layout.rows.map((row, ri) => {
            if (ri !== found.rowIndex) return row;
            return {
              ...row,
              children: (row.children || []).map((c) =>
                c.id === found.stack.id ? { ...c, children: nextKids } : c
              ),
            };
          });
          return { ...prev, layout: { ...prev.layout, rows } };
        }
        // Moving past stack edge → eject below / above the QR row
        if (dir > 0 && found.stackIndex === kids.length - 1) {
          const { rows: without, removed } = removeLeafFromRows(
            prev.layout.rows,
            selectedId
          );
          if (!removed) return prev;
          return {
            ...prev,
            layout: {
              ...prev.layout,
              rows: insertLeafAsRow(without, found.rowIndex + 1, {
                ...removed,
                span: 12,
              }),
            },
          };
        }
        if (dir < 0 && found.stackIndex === 0) {
          const { rows: without, removed } = removeLeafFromRows(
            prev.layout.rows,
            selectedId
          );
          if (!removed) return prev;
          return {
            ...prev,
            layout: {
              ...prev.layout,
              rows: insertLeafAsRow(without, found.rowIndex, {
                ...removed,
                span: 12,
              }),
            },
          };
        }
        return prev;
      }

      const flat = flattenLeaves(prev.layout.rows);
      const idx = flat.findIndex((c) => c.id === selectedId);
      if (idx < 0) return prev;
      const target = idx + dir;
      if (target < 0 || target >= flat.length) return prev;
      const next = arrayMove(flat, idx, target);
      return {
        ...prev,
        layout: {
          ...prev.layout,
          rows: packComponentsIntoRows(next, { fold: true }),
        },
      };
    });
  };

  /** Drag leaf: keep beside-stack membership stable; eject when dropped outside. */
  const onLeafDrag = (activeId, overId) => {
    if (!activeId || !overId || activeId === overId) return;
    updateTemplate((prev) => {
      const rows = prev.layout.rows;
      const activeCtx = findComp(rows, activeId);
      const overCtx = findComp(rows, overId);
      if (!activeCtx || !overCtx) return prev;

      const sameStack =
        activeCtx.stack &&
        overCtx.stack &&
        activeCtx.stack.id === overCtx.stack.id;

      if (sameStack) {
        const kids = arrayMove(
          [...(activeCtx.stack.children || [])],
          activeCtx.stackIndex,
          overCtx.stackIndex
        );
        return {
          ...prev,
          layout: {
            ...prev.layout,
            rows: rows.map((row, ri) => {
              if (ri !== activeCtx.rowIndex) return row;
              return {
                ...row,
                children: (row.children || []).map((c) =>
                  c.id === activeCtx.stack.id ? { ...c, children: kids } : c
                ),
              };
            }),
          },
        };
      }

      // Eject from beside group → own row under / near drop target
      if (activeCtx.stack && !overCtx.stack) {
        const { rows: without, removed } = removeLeafFromRows(rows, activeId);
        if (!removed) return prev;
        const overIsTall =
          isTallMedia(overCtx.comp) && (overCtx.comp.span || 12) < 12;
        if (overIsTall) {
          // Dropped on the QR itself → stay beside it
          return {
            ...prev,
            layout: {
              ...prev.layout,
              rows: insertLeafBesideTall(
                without,
                overCtx.rowIndex,
                { ...removed, breakBeside: false },
                null
              ),
            },
          };
        }
        const insertAt =
          overCtx.rowIndex > activeCtx.rowIndex
            ? overCtx.rowIndex + 1
            : overCtx.rowIndex;
        return {
          ...prev,
          layout: {
            ...prev.layout,
            rows: insertLeafAsRow(without, insertAt, {
              ...removed,
              span: 12,
            }),
          },
        };
      }

      // Enter beside group (drop on stack item or QR)
      if (!activeCtx.stack && overCtx.stack) {
        const { rows: without, removed } = removeLeafFromRows(rows, activeId);
        if (!removed) return prev;
        return {
          ...prev,
          layout: {
            ...prev.layout,
            rows: insertLeafIntoStack(
              without,
              overCtx.stack.id,
              overCtx.stackIndex,
              removed
            ),
          },
        };
      }
      if (
        !activeCtx.stack &&
        !overCtx.stack &&
        isTallMedia(overCtx.comp) &&
        (overCtx.comp.span || 12) < 12
      ) {
        const { rows: without, removed } = removeLeafFromRows(rows, activeId);
        if (!removed) return prev;
        return {
          ...prev,
          layout: {
            ...prev.layout,
            rows: insertLeafBesideTall(without, overCtx.rowIndex, removed, null),
          },
        };
      }

      // Page-level reorder (neither in stack, or both top-level)
      const flat = flattenLeaves(rows);
      const oldIndex = flat.findIndex((c) => c.id === activeId);
      const newIndex = flat.findIndex((c) => c.id === overId);
      if (oldIndex < 0 || newIndex < 0) return prev;
      const next = arrayMove(flat, oldIndex, newIndex).map((c) => {
        if (c.id !== activeId) return c;
        // Keep breakBeside so fold won't suck this back beside QR
        return activeCtx.comp.breakBeside || !overCtx.stack
          ? { ...c, breakBeside: Boolean(activeCtx.comp.breakBeside) }
          : c;
      });
      return {
        ...prev,
        layout: {
          ...prev.layout,
          rows: packComponentsIntoRows(next, { fold: true }),
        },
      };
    });
  };

  const onEjectFromBeside = (leafId) => {
    const id = leafId || selectedId;
    if (!id) return;
    updateTemplate((prev) => {
      const found = findComp(prev.layout.rows, id);
      if (!found?.stack) return prev;
      const { rows: without, removed } = removeLeafFromRows(
        prev.layout.rows,
        id
      );
      if (!removed) return prev;
      return {
        ...prev,
        layout: {
          ...prev.layout,
          rows: insertLeafAsRow(without, found.rowIndex + 1, {
            ...removed,
            span: 12,
          }),
        },
      };
    });
  };

  const onJoinBeside = (leafId) => {
    const id = leafId || selectedId;
    if (!id) return;
    updateTemplate((prev) => {
      const found = findComp(prev.layout.rows, id);
      if (!found || found.stack) return prev;
      // Find nearest tall media row above
      let targetRow = -1;
      for (let r = found.rowIndex; r >= 0; r--) {
        const kids = prev.layout.rows[r].children || [];
        if (
          kids.some(
            (c) => c.type !== "stack" && isTallMedia(c) && (c.span || 12) < 12
          )
        ) {
          targetRow = r;
          break;
        }
      }
      if (targetRow < 0) {
        for (let r = 0; r < prev.layout.rows.length; r++) {
          const kids = prev.layout.rows[r].children || [];
          if (
            kids.some(
              (c) => c.type !== "stack" && isTallMedia(c) && (c.span || 12) < 12
            )
          ) {
            targetRow = r;
            break;
          }
        }
      }
      if (targetRow < 0) return prev;
      const { rows: without, removed } = removeLeafFromRows(
        prev.layout.rows,
        id
      );
      if (!removed) return prev;
      const next = insertLeafBesideTall(without, targetRow, {
        ...removed,
        breakBeside: false,
      });
      return { ...prev, layout: { ...prev.layout, rows: next } };
    });
  };

  const onSpanChange = (targetId, span) => {
    const nextSpan = Math.max(1, Math.min(12, Number(span) || 12));
    updateTemplate((prev) => {
      // Resize a leaf inside the beside-QR stack only (don't move the whole column)
      for (const row of prev.layout.rows) {
        for (const cell of row.children || []) {
          if (
            cell.type === "stack" &&
            (cell.children || []).some((c) => c.id === targetId)
          ) {
            const rows = prev.layout.rows.map((r) => ({
              ...r,
              children: (r.children || []).map((c) => {
                if (c.id !== cell.id || c.type !== "stack") return c;
                return {
                  ...c,
                  children: (c.children || []).map((s) =>
                    s.id === targetId ? { ...s, span: nextSpan } : s
                  ),
                };
              }),
            }));
            return { ...prev, layout: { ...prev.layout, rows } };
          }
        }
      }

      // Top-level leaf (incl. QR width)
      const flat = flattenLeaves(prev.layout.rows).map((c) =>
        c.id === targetId ? { ...c, span: nextSpan } : c
      );
      return {
        ...prev,
        layout: {
          ...prev.layout,
          rows: packComponentsIntoRows(flat),
        },
      };
    });
  };

  const onSave = () => {
    saveTemplate(merchantId, template);
    message.success(i18n.t("order.printTemplate.saved"));
  };

  const onExit = () => {
    Modal.confirm({
      title: i18n.t("order.printTemplate.exitConfirm"),
      onOk: () => history.push("/main/order/print-templates"),
    });
  };

  return (
    <div className="print-template-designer">
      <header className="print-template-designer__header">
        <div className="print-template-designer__header-left">
          <Button
            type="text"
            icon={<ArrowLeftOutlined />}
            onClick={onExit}
          />
          <Input
            className="print-template-designer__name"
            value={template.name}
            onChange={(e) =>
              setTemplate((t) => ({ ...t, name: e.target.value }))
            }
          />
        </div>
        <div className="print-template-designer__header-center">
          <Button
            type="text"
            icon={<UndoOutlined />}
            disabled={!historyStack.length}
            onClick={undo}
          />
          <Button
            type="text"
            icon={<RedoOutlined />}
            disabled={!futureStack.length}
            onClick={redo}
          />
          <Select
            value={template.paperSize}
            style={{ width: 100 }}
            onChange={(v) =>
              updateTemplate((t) => ({ ...t, paperSize: v }))
            }
          >
            <Option value="A4">A4</Option>
            <Option value="80mm">80mm</Option>
            <Option value="58mm">58mm</Option>
          </Select>
          <Select
            value={template.lang === "ug" ? "ug" : "zh"}
            style={{ width: 120 }}
            onChange={(v) =>
              updateTemplate((t) => ({ ...t, lang: v }))
            }
          >
            <Option value="zh">{i18n.t("order.printTemplate.langZh")}</Option>
            <Option value="ug">{i18n.t("order.printTemplate.langUg")}</Option>
          </Select>
          <Space size={4}>
            <Button
              type="text"
              icon={<ZoomOutOutlined />}
              onClick={() => setZoom((z) => Math.max(0.6, +(z - 0.1).toFixed(1)))}
            />
            <span>{Math.round(zoom * 100)}%</span>
            <Button
              type="text"
              icon={<ZoomInOutlined />}
              onClick={() => setZoom((z) => Math.min(1.4, +(z + 0.1).toFixed(1)))}
            />
          </Space>
        </div>
        <div className="print-template-designer__header-right">
          <Button
            onClick={() =>
              Modal.info({
                title: i18n.t("order.printTemplate.preview"),
                width: 900,
                content: (
                  <div style={{ maxHeight: "70vh", overflow: "auto" }}>
                    <TemplateCanvas
                      template={template}
                      order={previewOrder}
                      zoom={1}
                      editable={false}
                    />
                  </div>
                ),
              })
            }
          >
            {i18n.t("order.printTemplate.preview")}
          </Button>
          <Button type="primary" onClick={onSave}>
            {i18n.t("order.printTemplate.save")}
          </Button>
          <Button onClick={onExit}>{i18n.t("order.printTemplate.exit")}</Button>
        </div>
      </header>

      <div className="print-template-designer__body">
        <aside className="print-template-designer__left">
          <div className="print-template-designer__palette">
            <ComponentPalette onAdd={onAdd} />
          </div>
          <div className="print-template-designer__outline">
            <ComponentOutline
              rows={template.layout.rows}
              selectedId={selectedId}
              onSelect={setSelectedId}
              onMove={onMoveSelected}
              onCopy={onCopySelected}
              onDelete={onDeleteSelected}
            />
          </div>
        </aside>

        <main className="print-template-designer__canvas">
          <TemplateCanvas
            template={template}
            order={previewOrder}
            selectedId={selectedId}
            onSelect={setSelectedId}
            onLeafDrag={onLeafDrag}
            onEjectFromBeside={onEjectFromBeside}
            onSpanChange={onSpanChange}
            zoom={zoom}
          />
        </main>

        <aside className="print-template-designer__right">
          <PropertyPanel
            component={selected}
            inBesideStack={Boolean(selectedFound?.stack)}
            onChange={onChangeComp}
            onCopy={onCopySelected}
            onDelete={onDeleteSelected}
            onEjectFromBeside={() => onEjectFromBeside(selectedId)}
            onJoinBeside={() => onJoinBeside(selectedId)}
          />
        </aside>
      </div>
    </div>
  );
}
